import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Ban, CalendarOff, CheckCircle2, Eye, RefreshCw } from 'lucide-react';
import {
  backofficeExperienceApi,
  extractErrorMessage,
  formatCurrency,
  formatDateTime,
  formatNumber,
} from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { ExperienceModerationDto, ExperienceStatus, Tone } from '@kardume/types';
import {
  EXPERIENCE_MODERATION_ERROR_MESSAGES,
  EXPERIENCE_STATUS_LABELS,
  EXPERIENCE_TYPE_LABELS,
} from '@kardume/types';
import {
  ActionButton,
  Badge,
  Button,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  StatCard,
  Table,
  TableContainer,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';

/**
 * A fila de moderação de experiências (card #352; API do card #187 do core).
 *
 * ## Por que existe separada da moderação de anúncios
 *
 * São duas filas com critérios diferentes, e o core já expressa isso com uma
 * permissão própria (`ROLE_BACKOFFICE_EXPERIENCE_MODERATE`): no anúncio se
 * confere preço e regra comercial; aqui se confere se a descrição corresponde
 * ao que a pessoa vai **viver**. Quem julga uma não é necessariamente quem
 * julga a outra.
 *
 * ## O aviso que muda a resposta ao lojista
 *
 * `upcomingSessions == 0` não impede a aprovação - impede o efeito dela. A
 * consulta pública exige data futura, então aprovar uma experiência sem agenda
 * não a coloca na vitrine. Quem modera precisa saber disso ANTES de responder
 * ao lojista que "já está no ar", e por isso o aviso aparece na linha da fila e
 * de novo no modal de confirmação.
 *
 * ## O motivo da recusa é obrigatório
 *
 * O core recusa sem ele, e a tela também: editar uma experiência recusada a
 * devolve automaticamente para a fila, então sem saber o que consertar o
 * lojista reenvia igual e a fila roda em círculo.
 *
 * ## O que a moderação NÃO é
 *
 * Conferência de descrição, não auditoria: higiene, segurança e licença não
 * são olhadas aqui, e o selo Kardume não promete qualidade. "Verificada",
 * "oficial" e "confiável" continuam palavras proibidas na copy.
 */

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Experience = styled.div`
  min-width: 0;

  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  .merchant {
    font-size: 0.8rem;
    color: ${(p) => p.theme.textSecondary};
  }
  .slug {
    font-family: monospace;
    font-size: 0.68rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Seals = styled.div`
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
  margin-top: 0.3rem;
`;

/** O contador de datas futuras, em destaque quando é zero. */
const Sessions = styled.span<{ $none: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  font-weight: ${(p) => (p.$none ? 700 : 500)};
  color: ${(p) => (p.$none ? p.theme.error : p.theme.textSecondary)};
  white-space: nowrap;
`;

const Detail = styled.div`
  display: grid;
  gap: 0.9rem;

  dl {
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 0.4rem 1rem;
    margin: 0;
    font-size: 0.88rem;
  }
  dt {
    color: ${(p) => p.theme.textMuted};
  }
  dd {
    margin: 0;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
`;

const TextBlock = styled.div`
  padding: 0.85rem 0.95rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  display: grid;
  gap: 0.4rem;

  .field {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${(p) => p.theme.textMuted};
  }
  p {
    margin: 0;
    font-size: 0.88rem;
    line-height: 1.55;
    color: ${(p) => p.theme.textPrimary};
    white-space: pre-wrap;
  }
`;

/** Aviso de "aprovar não publica": a informação mais importante do detalhe. */
const Warning = styled.div`
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.75rem 0.9rem;
  border: 1px solid ${(p) => p.theme.warning};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.warning}14;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
    color: ${(p) => p.theme.warning};
  }
  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.55;
  color: ${(p) => p.theme.textSecondary};
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 110px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const Counter = styled.div`
  margin-top: 0.3rem;
  text-align: right;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.textMuted};
`;

const STATUS_FILTER: Array<{ value: string; label: string }> = [
  { value: 'PENDING_REVIEW', label: 'Esperando decisão' },
  { value: '', label: 'Todas as situações' },
  { value: 'ACTIVE', label: 'No ar' },
  { value: 'REJECTED', label: 'Recusadas' },
  { value: 'PAUSED', label: 'Pausadas pela loja' },
  { value: 'ARCHIVED', label: 'Encerradas' },
  { value: 'DRAFT', label: 'Rascunhos' },
];

const PAGE_SIZE = 20;

/** Mesmo teto do `varchar` do motivo no core, validado aqui para poupar um request. */
const REASON_MAX_LENGTH = 500;

function statusTone(status: ExperienceStatus): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_REVIEW' || status === 'PAUSED' || status === 'DRAFT') return 'warning';
  if (status === 'REJECTED') return 'danger';
  return 'secondary';
}

type DecisionKind = 'approve' | 'reject';

const ExperienceModerationPage: React.FC = () => {
  const { showNotification } = useNotification();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState<string>('PENDING_REVIEW');
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState<ExperienceModerationDto | null>(null);
  const [deciding, setDeciding] = useState<DecisionKind | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data, loading, error, reload } = useAsync(
    () =>
      backofficeExperienceApi.list({
        page,
        size: PAGE_SIZE,
        query: debouncedSearch.trim() || undefined,
        status: (statusFilter || undefined) as ExperienceStatus | undefined,
      }),
    [page, debouncedSearch, statusFilter]
  );

  /**
   * Os contadores vêm de três consultas de uma linha cada, e não da página
   * carregada: a fila é paginada no servidor, então contar o que veio na tela
   * mostraria "20 esperando decisão" sempre que houvesse mais de vinte.
   */
  const counters = useAsync(
    () =>
      Promise.all([
        backofficeExperienceApi.list({ size: 1, status: 'PENDING_REVIEW' }),
        backofficeExperienceApi.list({ size: 1, status: 'ACTIVE' }),
        backofficeExperienceApi.list({ size: 1, status: 'REJECTED' }),
      ]).then(([pending, active, rejected]) => ({
        pending: pending.totalElements,
        active: active.totalElements,
        rejected: rejected.totalElements,
      })),
    []
  );

  const rows = useMemo(() => data?.content ?? [], [data]);

  const changeFilter = (next: string): void => {
    setStatusFilter(next);
    setPage(0);
  };

  const changeSearch = (next: string): void => {
    setSearch(next);
    setPage(0);
  };

  const refreshAll = (): void => {
    reload();
    counters.reload();
  };

  const openDecision = (kind: DecisionKind): void => {
    setReason('');
    setDeciding(kind);
  };

  const confirmDecision = async (): Promise<void> => {
    if (!detail || !deciding) return;
    const approved = deciding === 'approve';
    setSubmitting(true);
    try {
      const updated = await backofficeExperienceApi.moderate(detail.id, {
        approved,
        rejectionReason: approved ? undefined : reason.trim(),
      });
      showNotification(
        approved ? 'Experiência aprovada.' : 'Experiência recusada - o motivo foi registrado.',
        'success'
      );
      setDeciding(null);
      // Mantém o detalhe aberto com a resposta do servidor: é ela que passa a
      // mostrar quem decidiu, quando, e o motivo escrito.
      setDetail(updated);
      refreshAll();
    } catch (err) {
      showNotification(
        extractErrorMessage(
          err,
          approved ? 'Não foi possível aprovar a experiência.' : 'Não foi possível recusar a experiência.',
          EXPERIENCE_MODERATION_ERROR_MESSAGES
        ),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const priceLabel = (experience: ExperienceModerationDto): string =>
    experience.free ? 'Gratuita' : formatCurrency(experience.price);

  const noUpcoming = detail?.upcomingSessions === 0;
  const isPending = detail?.status === 'PENDING_REVIEW';

  return (
    <>
      <PageHeader
        title="Moderação de experiências"
        subtitle="O que as lojas cadastraram para a cidade viver. Confere-se se a descrição corresponde ao que a loja entrega - não higiene, segurança nem licença."
      />

      <StatsGrid>
        <StatCard label="Esperando decisão" value={formatNumber(counters.data?.pending)} />
        <StatCard label="No ar" value={formatNumber(counters.data?.active)} />
        <StatCard label="Recusadas" value={formatNumber(counters.data?.rejected)} />
        <StatCard label="Nesta busca" value={formatNumber(data?.totalElements)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por título da experiência ou nome da loja..."
          value={search}
          onChange={(e) => changeSearch(e.target.value)}
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => changeFilter(e.target.value)}
        />
        <ActionButton title="Atualizar" onClick={refreshAll}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={rows.length === 0}
        emptyTitle="Nenhuma experiência nesta situação"
        emptyDescription="Quando uma loja cadastrar uma experiência, ela aparece aqui esperando decisão."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Experiência</th>
                <th>Formato</th>
                <th>Preço</th>
                <th>Datas futuras</th>
                <th>Situação</th>
                <th>Enviada em</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((experience) => (
                <tr key={experience.id}>
                  <td>
                    <Experience>
                      <strong>{experience.title}</strong>
                      <div className="merchant">{experience.merchantName}</div>
                      <div className="slug">/{experience.slug}</div>
                      {(experience.kardumeExclusive || experience.requiredPassPlanName) && (
                        <Seals>
                          {experience.kardumeExclusive && <Badge tone="info">Exclusiva Kardume</Badge>}
                          {experience.requiredPassPlanName && (
                            <Badge tone="primary">Pass {experience.requiredPassPlanName}</Badge>
                          )}
                        </Seals>
                      )}
                    </Experience>
                  </td>
                  <td>
                    <Badge tone="secondary">{EXPERIENCE_TYPE_LABELS[experience.type]}</Badge>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{priceLabel(experience)}</td>
                  <td>
                    <Sessions
                      $none={experience.upcomingSessions === 0}
                      title={
                        experience.upcomingSessions === 0
                          ? 'Sem data futura: aprovar não a coloca na vitrine'
                          : undefined
                      }
                    >
                      {experience.upcomingSessions === 0 && <CalendarOff size={14} />}
                      {formatNumber(experience.upcomingSessions)}
                    </Sessions>
                  </td>
                  <td>
                    <Badge tone={statusTone(experience.status)}>
                      {EXPERIENCE_STATUS_LABELS[experience.status]}
                    </Badge>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(experience.createdAt)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionButton title="Ver detalhe" onClick={() => setDetail(experience)}>
                      <Eye size={16} />
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>

        {(data?.totalPages ?? 0) > 1 && (
          <Pagination
            currentPage={(data?.number ?? 0) + 1}
            totalPages={data?.totalPages ?? 1}
            totalItems={data?.totalElements}
            pageSize={PAGE_SIZE}
            onPageChange={(next) => setPage(next - 1)}
          />
        )}
      </DataState>

      {/* Detalhe: tudo que a loja cadastrou, porque é o texto que se está
          julgando - o título sozinho nunca diz se a descrição corresponde ao
          que a pessoa vai viver. */}
      <Modal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title="Experiência enviada pela loja"
        width="720px"
      >
        {detail && (
          <Detail>
            <TextBlock>
              <span className="field">{EXPERIENCE_TYPE_LABELS[detail.type]}</span>
              <p>
                <strong>{detail.title}</strong>
              </p>
              <Seals>
                <Badge tone={statusTone(detail.status)}>{EXPERIENCE_STATUS_LABELS[detail.status]}</Badge>
                {detail.kardumeExclusive && <Badge tone="info">Exclusiva Kardume</Badge>}
                {detail.requiredPassPlanName && (
                  <Badge tone="primary">Exclusiva Pass a partir de {detail.requiredPassPlanName}</Badge>
                )}
              </Seals>
            </TextBlock>

            {noUpcoming && (
              <Warning>
                <CalendarOff size={16} />
                <span>
                  <strong>Sem data futura marcada.</strong> Aprovar não coloca esta experiência na
                  vitrine - a consulta pública exige uma data ainda por acontecer. Ao responder à loja,
                  diga que ela precisa abrir a agenda.
                </span>
              </Warning>
            )}

            <dl>
              <dt>Loja</dt>
              <dd>{detail.merchantName}</dd>
              <dt>Categoria</dt>
              <dd>{detail.categoryName || '-'}</dd>
              <dt>Preço</dt>
              <dd>{priceLabel(detail)}</dd>
              <dt>Datas futuras</dt>
              <dd>{formatNumber(detail.upcomingSessions)}</dd>
              <dt>Endereço público</dt>
              <dd>/{detail.slug}</dd>
              <dt>Enviada em</dt>
              <dd>{formatDateTime(detail.createdAt)}</dd>
              {detail.moderatedAt && (
                <>
                  <dt>Decidida em</dt>
                  <dd>{formatDateTime(detail.moderatedAt)}</dd>
                </>
              )}
              {detail.moderatedBy && (
                <>
                  <dt>Decidida por</dt>
                  {/* O core devolve só o identificador de quem julgou; não há
                      rota que troque esse id pelo nome sem exigir a permissão
                      de funcionários, que quem modera pode não ter. */}
                  <dd title={detail.moderatedBy}>{detail.moderatedBy.slice(0, 8)}…</dd>
                </>
              )}
            </dl>

            <TextBlock>
              <span className="field">Descrição</span>
              <p>{detail.description?.trim() || 'A loja não escreveu descrição.'}</p>
            </TextBlock>

            <TextBlock>
              <span className="field">Regras</span>
              <p>{detail.rules?.trim() || 'A loja não escreveu regras.'}</p>
            </TextBlock>

            {detail.rejectionReason && (
              <TextBlock>
                <span className="field">Motivo da recusa</span>
                <p>{detail.rejectionReason}</p>
              </TextBlock>
            )}

            {isPending ? (
              <ActionsRow>
                <Button variant="primary" onClick={() => openDecision('approve')}>
                  <CheckCircle2 size={16} /> Aprovar
                </Button>
                <Button variant="outline" onClick={() => openDecision('reject')}>
                  <Ban size={16} /> Recusar
                </Button>
              </ActionsRow>
            ) : (
              <Hint>
                Só o que está esperando decisão pode ser moderado. Esta experiência está{' '}
                {EXPERIENCE_STATUS_LABELS[detail.status].toLowerCase()} - pausar e retomar são da loja.
              </Hint>
            )}
          </Detail>
        )}
      </Modal>

      <Modal
        isOpen={deciding !== null}
        onClose={() => setDeciding(null)}
        title={deciding === 'approve' ? 'Aprovar experiência' : 'Recusar experiência'}
        width="540px"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeciding(null)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              variant={deciding === 'approve' ? 'primary' : 'danger'}
              // O core recusa a decisão sem motivo. Desabilitar aqui evita que
              // o operador descubra isso por um erro depois de clicar.
              disabled={submitting || (deciding === 'reject' && reason.trim().length === 0)}
              isLoading={submitting}
              onClick={confirmDecision}
            >
              {deciding === 'approve' ? 'Aprovar' : 'Recusar'}
            </Button>
          </>
        }
      >
        {deciding === 'approve' ? (
          <Detail>
            <Hint>
              A experiência passa a valer para o público. Confira se a descrição corresponde ao que a
              loja entrega - não é uma auditoria de higiene, segurança ou licença, e o Kardume não
              promete qualidade.
            </Hint>
            {noUpcoming && (
              <Warning>
                <CalendarOff size={16} />
                <span>
                  Esta experiência <strong>não tem data futura</strong>: mesmo aprovada, ela não aparece
                  na vitrine até a loja abrir a agenda.
                </span>
              </Warning>
            )}
          </Detail>
        ) : (
          <div>
            <Hint style={{ marginBottom: '0.75rem' }}>
              O motivo chega ao lojista e é o que ele vai corrigir. Editar uma experiência recusada a
              devolve automaticamente para esta fila - sem um motivo claro, ela volta igual.
            </Hint>
            <FieldLabel htmlFor="experience-rejection-reason">Motivo da recusa</FieldLabel>
            <Textarea
              id="experience-rejection-reason"
              value={reason}
              maxLength={REASON_MAX_LENGTH}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
              placeholder="Ex.: a descrição fala em jantar harmonizado, mas as regras dizem que a bebida não está inclusa. Ajuste um dos dois."
            />
            <Counter>
              {reason.length}/{REASON_MAX_LENGTH}
            </Counter>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ExperienceModerationPage;
